import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Send, User, Mail, Phone, Calendar, Euro, Palette, FileText, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import useInView from '../hooks/useInView';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations/translations';

const ModernForm = () => {
  const { language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [formRef, formInView] = useInView();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    projectDescription: '',
    serviceType: '',
    deadline: '',
    budget: '',
    name: '',
    email: '',
    phone: ''
  });

  // Configuration EmailJS
  const EMAILJS_SERVICE_ID = 'service_2xgxq6u';
  const EMAILJS_TEMPLATE_ID = 'template_h4mxfee';
  const EMAILJS_PUBLIC_KEY = '_dd0hiGGRLY-VlEBu';

  const steps = [
    {
      id: 1,
      title: getTranslation(language, 'form.step1.title'),
      icon: <FileText className="w-6 h-6" />,
      field: 'projectDescription',
      type: 'textarea',
      placeholder: getTranslation(language, 'form.step1.placeholder'),
      description: getTranslation(language, 'form.step1.description')
    },
    {
      id: 2,
      title: getTranslation(language, 'form.step2.title'),
      icon: <Palette className="w-6 h-6" />,
      field: 'serviceType',
      type: 'radio',
      options: [
        { value: 'design', label: getTranslation(language, 'form.step2.options.design.label'), description: getTranslation(language, 'form.step2.options.design.description') },
        { value: 'animation', label: getTranslation(language, 'form.step2.options.animation.label'), description: getTranslation(language, 'form.step2.options.animation.description') },
        { value: 'nocode', label: getTranslation(language, 'form.step2.options.nocode.label'), description: getTranslation(language, 'form.step2.options.nocode.description') }
      ]
    },
    {
      id: 3,
      title: getTranslation(language, 'form.step3.title'),
      icon: <Calendar className="w-6 h-6" />,
      field: 'deadline',
      type: 'radio',
      options: [
        { value: 'urgent', label: getTranslation(language, 'form.step3.options.urgent.label'), description: getTranslation(language, 'form.step3.options.urgent.description') },
        { value: 'normal', label: getTranslation(language, 'form.step3.options.normal.label'), description: getTranslation(language, 'form.step3.options.normal.description') },
        { value: 'flexible', label: getTranslation(language, 'form.step3.options.flexible.label'), description: getTranslation(language, 'form.step3.options.flexible.description') },
        { value: 'discuss', label: getTranslation(language, 'form.step3.options.discuss.label'), description: getTranslation(language, 'form.step3.options.discuss.description') }
      ]
    },
    {
      id: 4,
      title: getTranslation(language, 'form.step4.title'),
      icon: <Euro className="w-6 h-6" />,
      field: 'budget',
      type: 'radio',
      options: [
        { value: 'small', label: getTranslation(language, 'form.step4.options.small.label'), description: getTranslation(language, 'form.step4.options.small.description') },
        { value: 'medium', label: getTranslation(language, 'form.step4.options.medium.label'), description: getTranslation(language, 'form.step4.options.medium.description') },
        { value: 'large', label: getTranslation(language, 'form.step4.options.large.label'), description: getTranslation(language, 'form.step4.options.large.description') }
      ]
    },
    {
      id: 5,
      title: getTranslation(language, 'form.step5.title'),
      icon: <User className="w-6 h-6" />,
      field: 'contact',
      type: 'contact',
      fields: [
        { name: 'name', icon: <User className="w-5 h-5" />, placeholder: getTranslation(language, 'form.step5.fields.name'), type: 'text' },
        { name: 'email', icon: <Mail className="w-5 h-5" />, placeholder: getTranslation(language, 'form.step5.fields.email'), type: 'email' },
        { name: 'phone', icon: <Phone className="w-5 h-5" />, placeholder: getTranslation(language, 'form.step5.fields.phone'), type: 'tel' }
      ]
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const isStepComplete = (stepIndex) => {
    const step = steps[stepIndex];
    switch (step.field) {
      case 'projectDescription':
        return formData.projectDescription.trim().length > 0;
      case 'serviceType':
        return formData.serviceType !== '';
      case 'deadline':
        return formData.deadline !== '';
      case 'budget':
        return formData.budget !== '';
      case 'contact':
        return formData.name.trim() !== '' && 
               formData.email.trim() !== '' && 
               formData.phone.trim() !== '';
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1 && isStepComplete(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isStepComplete(currentStep)) return;

    setIsSubmitting(true);

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone,
        project_description: formData.projectDescription,
        service_type: getServiceLabel(formData.serviceType),
        deadline: getDeadlineLabel(formData.deadline),
        budget: getBudgetLabel(formData.budget),
        to_name: 'Wassim'
      };

      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log('Email envoyé avec succès!', response.status, response.text);
      setIsSubmitted(true);
      
      setTimeout(() => {
        setIsSubmitted(false);
        setCurrentStep(0);
        setFormData({
          projectDescription: '',
          serviceType: '',
          deadline: '',
          budget: '',
          name: '',
          email: '',
          phone: ''
        });
      }, 5000);

    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      alert('Une erreur est survenue lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getServiceLabel = (value) => {
    const option = steps[1].options.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const getDeadlineLabel = (value) => {
    const option = steps[2].options.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const getBudgetLabel = (value) => {
    const option = steps[3].options.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="relative backdrop-blur-xl bg-gradient-to-br from-gray-900/80 to-black/80 border border-[#DC1A5F]/30 rounded-3xl shadow-2xl p-12 max-w-md w-full text-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#DC1A5F]/5 to-transparent"></div>
          <div className="relative z-10">
            <div className="mb-6 inline-block">
              <div className="relative">
                <div className="absolute inset-0 bg-[#DC1A5F] blur-2xl opacity-50 animate-pulse"></div>
                <CheckCircle className="relative w-20 h-20 text-[#DC1A5F] animate-bounce" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4 tracking-tight">
              {getTranslation(language, 'form.success.title')}
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              {getTranslation(language, 'form.success.message')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black flex items-center justify-center p-4">
      <div 
        ref={formRef}
        className={`w-full max-w-4xl transition-all duration-1000 ${
          formInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        {/* Progress bar moderne */}
        <div className="mb-10">
          <div className="flex justify-between items-center mb-4">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {getTranslation(language, 'form.progress.step')} {currentStep + 1}/{steps.length}
            </span>
            <span className="text-xs font-bold text-[#DC1A5F] bg-[#DC1A5F]/10 px-3 py-1 rounded-full">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="relative h-1.5 bg-gray-800/50 rounded-full overflow-hidden backdrop-blur-sm">
            <div 
              className="absolute h-full bg-gradient-to-r from-[#DC1A5F] via-[#B91651] via-[#32D6E0] to-[#2AB5C4] rounded-full transition-all duration-700 ease-out shadow-lg shadow-[#DC1A5F]/50"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
            </div>
          </div>
        </div>

        {/* Carte principale avec glassmorphism */}
        <div className="relative backdrop-blur-2xl bg-gradient-to-br from-gray-900/50 to-black/50 border border-gray-800/50 rounded-3xl shadow-2xl overflow-hidden hover:scale-105 hover:shadow-[0_20px_60px_rgba(220,26,95,0.4)] hover:border-[#DC1A5F] hover:-translate-y-2 transition-all duration-300 ease-out">
          {/* Glow effect */}
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#DC1A5F] rounded-full blur-3xl opacity-10"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-500 rounded-full blur-3xl opacity-10"></div>
          
          {/* Header minimal */}
          <div className="relative p-8 border-b border-gray-800/50">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                {currentStepData.title}
              </h2>
              {currentStepData.description && (
                <p className="text-sm text-gray-400 mt-2">
                  {currentStepData.description}
                </p>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="relative p-8 md:p-10">
            {currentStepData.type === 'textarea' && (
              <div className="space-y-3">
                <textarea
                  value={formData[currentStepData.field]}
                  onChange={(e) => handleInputChange(currentStepData.field, e.target.value)}
                  placeholder={currentStepData.placeholder}
                  rows={6}
                  className="w-full px-5 py-4 bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl focus:border-[#DC1A5F] focus:ring-2 focus:ring-[#DC1A5F]/20 focus:bg-gray-900/70 outline-none transition-all resize-none text-white placeholder-gray-500 text-base leading-relaxed"
                />
              </div>
            )}

            {currentStepData.type === 'radio' && (
              <div className="space-y-3">
                {currentStepData.options.map((option, index) => (
                  <label
                    key={option.value}
                    className={`group relative block p-5 border rounded-2xl cursor-pointer transition-all duration-300 overflow-hidden ${
                      formData[currentStepData.field] === option.value
                        ? 'border-[#DC1A5F] bg-gradient-to-br from-[#DC1A5F]/10 to-pink-500/10 shadow-lg shadow-[#DC1A5F]/20 scale-[1.02]'
                        : 'border-gray-700/50 hover:border-[#DC1A5F]/50 hover:bg-gray-800/30 backdrop-blur-sm'
                    }`}
                    style={{ 
                      transform: formData[currentStepData.field] === option.value ? 'translateX(0)' : '',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    {formData[currentStepData.field] === option.value && (
                      <div className="absolute inset-0 bg-gradient-to-r from-[#DC1A5F]/0 via-[#DC1A5F]/5 to-[#DC1A5F]/0 animate-pulse"></div>
                    )}
                    <div className="relative flex items-start gap-4">
                      <div className="flex items-center justify-center mt-0.5">
                        <input
                          type="radio"
                          name={currentStepData.field}
                          value={option.value}
                          checked={formData[currentStepData.field] === option.value}
                          onChange={(e) => handleInputChange(currentStepData.field, e.target.value)}
                          className="w-5 h-5 text-[#DC1A5F] bg-gray-900 border-gray-600 focus:ring-[#DC1A5F] focus:ring-offset-0"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-white text-base group-hover:text-[#DC1A5F] transition-colors">
                          {option.label}
                        </div>
                        {option.description && (
                          <div className="text-sm text-gray-400 mt-1.5 leading-relaxed">
                            {option.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {currentStepData.type === 'contact' && (
              <div className="space-y-4">
                {currentStepData.fields.map((field) => (
                  <div key={field.name} className="relative group">
                    <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-[#DC1A5F] transition-colors">
                      {field.icon}
                    </div>
                    <input
                      type={field.type}
                      value={formData[field.name]}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      placeholder={field.placeholder}
                      className="w-full pl-14 pr-5 py-4 bg-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl focus:border-[#DC1A5F] focus:ring-2 focus:ring-[#DC1A5F]/20 focus:bg-gray-900/70 outline-none transition-all text-white placeholder-gray-500 text-base"
                      required={currentStep === steps.length - 1}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-4 mt-10">
              {currentStep > 0 && (
                <Button
                  type="button"
                  onClick={handlePrev}
                  variant="outline"
                  className="group flex-1 py-5 text-base font-semibold border border-gray-700/50 backdrop-blur-sm hover:bg-gray-800/50 hover:border-gray-600 text-white transition-all rounded-xl"
                >
                  <ChevronLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
                  {getTranslation(language, 'form.buttons.previous')}
                </Button>
              )}

              {currentStep < steps.length - 1 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  disabled={!isStepComplete(currentStep)}
                  className={`group relative overflow-hidden flex-1 py-5 text-base font-semibold ${
                    currentStep === 0 ? 'w-full' : ''
                  } ${
                    !isStepComplete(currentStep)
                      ? 'opacity-50 cursor-not-allowed bg-gray-800 border-0'
                      : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-xl shadow-green-500/30 border-0 hover:shadow-green-500/50 hover:scale-[1.02]'
                  } text-white transition-all rounded-xl`}
                  style={!isStepComplete(currentStep) ? {} : {
                    background: 'linear-gradient(to right, #22C55E, #10B981)',
                    border: 'none'
                  }}
                >
                  {!isStepComplete(currentStep) ? null : (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  )}
                  <span className="relative flex items-center justify-center">
                    {getTranslation(language, 'form.buttons.next')}
                    <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={!isStepComplete(currentStep) || isSubmitting}
                  className={`group relative overflow-hidden flex-1 py-5 text-base font-semibold ${
                    !isStepComplete(currentStep) || isSubmitting
                      ? 'opacity-50 cursor-not-allowed bg-gray-800 border-0'
                      : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-xl shadow-green-500/30 border-0 hover:shadow-green-500/50 hover:scale-[1.02]'
                  } text-white transition-all rounded-xl`}
                  style={(!isStepComplete(currentStep) || isSubmitting) ? {} : {
                    background: 'linear-gradient(to right, #22C55E, #10B981)',
                    border: 'none'
                  }}
                >
                  {(!isStepComplete(currentStep) || isSubmitting) ? null : (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  )}
                  <span className="relative flex items-center justify-center">
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        {getTranslation(language, 'form.buttons.sending')}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        {getTranslation(language, 'form.buttons.submit')}
                      </>
                    )}
                  </span>
                </Button>
              )}
            </div>
          </form>
        </div>

        {/* Step indicators modernes */}
        <div className="flex justify-center gap-2.5 mt-8">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`rounded-full transition-all duration-500 ${
                index === currentStep
                  ? 'w-10 h-2 bg-gradient-to-r from-[#DC1A5F] to-pink-600 shadow-lg shadow-[#DC1A5F]/50'
                  : index < currentStep
                  ? 'w-2 h-2 bg-[#DC1A5F]'
                  : 'w-2 h-2 bg-gray-700/50'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ModernForm;